import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Markdown from 'markdown-to-jsx'
import parse from 'html-react-parser'
import { parseISO } from 'date-fns'
import { Box, Button, Icon, Link, ListGrid, ListGridItem, Text, P, Tooltip } from '@maui/elements'
import withFreemium from '@/components/common/Freemium/WithFreemium'
import { closeModal, openModal } from '@/events'
import { capitalize, stripHtml, formatMediumDate } from '@/utilities'
import ActorIcon from '@/components/icons/ActorIcon'
import { HTMLDisplay } from '../../common'

const noDataAvailable = 'No data available'

const onActorClick = (actor) => {
  closeModal()
  openModal('ActorModal', actor.id, actor.name)
}

const onMalwareClick = (malware) => {
  closeModal()
  openModal('MalwareModal', malware.id, malware.name)
}

// ShortenText
const ShortenText = ({ text, clamp = 3 }) => {
  const [showText, setShowText] = useState(false)
  const [showExpander, setShowExpander] = useState(false)
  const ref = useRef(null)

  // If the content is not at least 3 lines hide the expander
  useEffect(() => {
    const t = setTimeout(() => {
      if (ref.current.clientHeight) {
        setShowExpander(ref.current?.scrollHeight > ref.current.clientHeight)
      }
    })
    return () => {
      clearTimeout(t)
    }
  }, [ref])

  const style = !showText && text
    ? {
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: clamp,
        display: '-webkit-box',
        overflow: 'hidden',
      }
    : {}

  return (
    <>
      <Box ref={ref} style={style}>
        <Markdown
          options={{
            overrides: {
              p: {
                props: { style: { margin: '0px' } }
              },
              ul: {
                props: { style: { margin: '10px 0 0 0' } }
              },
              em: {
                props: { style: { display: 'flex' } }
              },
              a: {
                props: { style: { display: 'inline-flex' } }
              }
            }
          }}
        >
          {text.replace(/[_]/g, '-')}
        </Markdown>

      </Box>
      { showExpander && showText &&
        <Button
          pl="0"
          variant="primaryText"
          onClick={() => setShowText(false)}
        >
          Show less
        </Button>
      }
      { showExpander && !showText &&
        <Button
          pl="0"
          variant="primaryText"
          onClick={() => setShowText(true)}
        >
          Show more
        </Button>
      }
    </>
  )
}

ShortenText.propTypes = {
  text: PropTypes.string
}

// Sources
const Sources = ({ sources }) => {
  return sources.map((source, i) => (
    <Link
      key={i}
      color='colorActivePrimary'
      href={source.url}
      alt="Open full details"
      target="_blank"
      rel="noopener noreferrer"
      title={source.source_description}
      display="flex"
      alignItems="center"
      gap="8px"
      marginBottom="4px"
    >
      <Icon name="external-link" />
      <Text>{source.source_name}</Text>
    </Link>
  ))
}

// Vendor Details
const VendorDetails = ({ vendorFixDetails }) => {
  if (!vendorFixDetails || !vendorFixDetails.length || !Array.isArray(vendorFixDetails)) return noDataAvailable
  return vendorFixDetails.map((detail, i) => {
    return (
      <Box key={i} marginBottom="spaceGapMedium">
        <Icon name='notepad' marginRight="spaceGapMedium" />
        <Box
          as="a"
          color="colorTextMain"
          href={detail.url}
          target='_blank'
        >
          {detail.name}
        </Box>
      </Box>
    )
  })
}

const RatingValue = ({ children, color }) => {
  return (
    <Box fontWeight='fontWeightMedium' fontSize='textAvatarMediumFontSize' lineHeight='textAvatarMediumLineHeight' color={color} display='flex' alignItems='center'>
      {children}
    </Box>
  )
}

RatingValue.propTypes = {
  children: PropTypes.node,
  color: PropTypes.string
}

export const RatingValueWithFreemium = withFreemium({ prop: 'value', layout: 'inline' })(({ color, value, isPredicted }) =>
  <RatingValue color={color}>
    {capitalize((Array.isArray(value) ? value[0] : value) || '')}
    {isPredicted && <Text color='colorTextDeemphasis' fontSize='textBody2FontSize' lineHeight='typographyBodySmallLineHeight' ml='spaceGapSmall'>[Predicted]</Text>}
  </RatingValue>
)

export const YesOrNoWithFreemium = withFreemium({ prop: 'value', layout: 'inline' })(({ color, value }) =>
  <RatingValue color={color}>
    {value ? 'Yes' : 'No'}
  </RatingValue>
)

export const VulnType = ({ vulnType }) => {
  const { id, title } = vulnType || {}
  const updatedIdWithoutCWE = id?.replace('CWE-', '')
  const url = `https://cwe.mitre.org/data/definitions/${updatedIdWithoutCWE}.html`
  return (
    <Link
      as ='a'
      href={url}
      target="_blank"
      rel="noopener noreferrer"
    >
      <Icon name="external-link" size="md" mr="spaceGapSmall" />
      {title} {`(${id})`}
    </Link>
  )
}
export const SummaryWithFreemium = withFreemium({ prop: 'summary', layout: 'fullCenter' })(({ summary }) => <HTMLDisplay html={summary} />)
export const DescriptionWithFreemium = withFreemium({ prop: 'description', layout: 'fullCenter' })(({ description }) => <ShortenText text={stripHtml(description)} />)
export const AnalysisWithFreemium = withFreemium({ prop: 'analysis', layout: 'fullCenter' })(({ analysis }) => <ShortenText text={analysis} clamp={8}/>)
export const DisclosureDateWithFreemium = withFreemium({ prop: 'disclosureDate', layout: 'fullCenter' })(({ disclosureDate }) => disclosureDate)
export const VulnTypeWithFreemium = withFreemium({ prop: 'vulnType', layout: 'fullCenter' })(({ vulnType }) => <VulnType vulnType={vulnType} />)
export const VulnProductsWithFreemium = withFreemium({ prop: 'vulnProducts', layout: 'fullCenter' })(({ vulnProducts }) => <HTMLDisplay html={vulnProducts}/>)
export const VenderDetailsWithFreemium = withFreemium({ prop: 'vendorFixDetails', layout: 'fullCenter' })(({ vendorFixDetails }) => <VendorDetails vendorFixDetails={vendorFixDetails} />)
export const ExploitConsequenceWithFreemium = withFreemium({ prop: 'exploitConsequence', layout: 'fullCenter' })(({ exploitConsequence }) => exploitConsequence)
export const MitigationWithFreemium = withFreemium({ prop: 'mitigation', layout: 'fullCenter' })(({ mitigation }) => mitigation.join(', '))
export const WorkaroundsWithFreemium = withFreemium({ prop: 'workarounds', layout: 'fullCenter' })(({ workarounds }) => <Box>{parse(workarounds)}</Box>)
export const SourcesWithFreemium = withFreemium({ prop: 'sources', layout: 'fullCenter' })(({ sources }) => <Sources sources={sources} />)

export const DaysToPatchWithFreemium = withFreemium({ prop: 'daysToPatch', layout: 'fullCenter' })(({ daysToPatch }) => {
  return (
    <P textStyle="bodyMain" mt="0">
      {daysToPatch}
    </P>
  )
})

export const EPSSWithFreemium = withFreemium({ prop: 'epss', layout: 'fullCenter' })(({ epss }) => {
  return (
    <Box display='flex' flexWrap="wrap" color="colorTextMain">
      <Box flexBasis="33%">
        <P textStyle="bodyMain" mt="0" mb="spaceGapMedium">
          EPSS Score
          <Tooltip
            ml="spaceGapSmall"
            color="white"
            portal
            size="large"
            textStyle="bodyMain"
            tooltip='EPSS Score provides a probability of observing exploitation activity in the next 30 days.'
          >
            <Icon name="info" verticalAlign="top" />
          </Tooltip>
        </P>
        <P textStyle="bodyMain" mt="0">
          {epss.score}
        </P>
      </Box>
      <Box flexBasis="33%">
        <P textStyle="bodyMain" mt="0" mb="spaceGapMedium">
          EPSS Percentile
          <Tooltip
            ml="spaceGapSmall"
            color="white"
            portal
            size="large"
            textStyle="bodyMain"
            tooltip='EPSS Percentile is a rank ordering of probabilities from highest to lowest. This ranking is based on every published CVE. A vulnerability with an EPSS percentile 92nd means that 92% of all CVEs are scored lower; in other words, this vulnerability EPSS Score is in the Top 8.'
          >
            <Icon name="info" verticalAlign="top" />
          </Tooltip>
        </P>
        <P textStyle="bodyMain" mt="0">
          {epss.percentile * 100}%
        </P>
      </Box>
    </Box>
  )
})

export const ActorsWithFreemium = withFreemium({ prop: 'actors', layout: 'fullCenter' })(({ actors }) => {
  const [showMore, setShowMore] = useState(false)
  const [showExpander, setShowExpander] = useState(false)
  const actorsList = showMore ? actors : actors?.slice(0, 15)
  useEffect(() => {
    if (actors?.length > 15) {
      setShowExpander(true)
    }
  }, [actors])
  return (
    <>
      <ListGrid list={actorsList} type='ActorModal' cellWidth={['100%', '50%', '20%']}>
        {item => (
          <ListGridItem overflow="hidden">
            <Link
              onClick={() => onActorClick(item)}
              display='flex'
              gap='8px'
              overflow='hidden'
              title={item.name}
            >
              <ActorIcon height='16px' width='16px' flex='none' countryCodeAvailable={item.country_code} size='sm' />
              <Text
                overflow='hidden'
                whiteSpace='nowrap'
                textOverflow='ellipsis'
              >{item.name}</Text>
            </Link>
          </ListGridItem>
        )}
      </ListGrid>
      { showExpander && showMore &&
        <Button
          pl="0"
          variant="primaryText"
          onClick={() => setShowMore(false)}
        >
          Show less
        </Button>
      }
      { showExpander && !showMore &&
        <Button
          pl="0"
          variant="primaryText"
          onClick={() => setShowMore(true)}
        >
          Show more
        </Button>
      }
    </>
  )
})
export const MalwareWithFreemium = withFreemium({ prop: 'malware', layout: 'fullCenter' })(({ malware }) => {
  const [showMore, setShowMore] = useState(false)
  const [showExpander, setShowExpander] = useState(false)
  const malwareList = showMore ? malware : malware?.slice(0, 15)
  useEffect(() => {
    if (malware?.length > 15) {
      setShowExpander(true)
    }
  }, [malware])
  return (
    <>
      <ListGrid list={malwareList} type='MalwareModal' cellWidth={['100%', '50%', '25%']}>
        {item => (
          <ListGridItem overflow="hidden">
            <Link
              onClick={() => onMalwareClick(item)}
              display='flex'
              gap='8px'
              overflow='hidden'
              title={item.name}
            >
              <Icon
                name='bug'
                flex='none'
                size='md'
              />
              <Text
                overflow='hidden'
                whiteSpace='nowrap'
                textOverflow='ellipsis'
              >{item.name}</Text>
            </Link>
          </ListGridItem>
        )}
      </ListGrid>
      { showExpander && showMore &&
        <Button
          pl="0"
          variant="primaryText"
          onClick={() => setShowMore(false)}
        >
          Show less
        </Button>
      }
      { showExpander && !showMore &&
        <Button
          pl="0"
          variant="primaryText"
          onClick={() => setShowMore(true)}
        >
          Show more
        </Button>
      }
    </>
  )
})
export const ToolsList = ({ tools }) => {
  const [showMore, setShowMore] = useState(false)
  const [showExpander, setShowExpander] = useState(false)
  const toolList = showMore ? tools : tools?.slice(0, 15)
  useEffect(() => {
    if (tools?.length > 15) {
      setShowExpander(true)
    }
  }, [tools])
  return (
    <>
      <ListGrid list={toolList} type='MalwareModal' cellWidth={['100%', '50%', '25%']}>
        {item => (
          <ListGridItem onClick={() => onMalwareClick(item)}>
            <Link display='flex' gap='8px'>
              <Icon name='tools' />
              {item.name}
            </Link>
          </ListGridItem>
        )}
      </ListGrid>
      { showExpander && showMore &&
        <Button
          pl="0"
          variant="primaryText"
          onClick={() => setShowMore(false)}
        >
          Show less
        </Button>
      }
      { showExpander && !showMore &&
        <Button
          pl="0"
          variant="primaryText"
          onClick={() => setShowMore(true)}
        >
          Show more
        </Button>
      }
    </>
  )
}
export const CISADates = ({ cisaKnownExploited }) => {
  return (
    <Box display='flex' flexWrap="wrap" color="colorTextMain">
      <Box flexBasis="33%">
        <P textStyle="bodyMain" color="colorTextDeemphasis" mt="0" mb="spaceGapMedium">
                Date Added to Catalog
        </P>
        <P textStyle="bodyMain" color="colorTextMain" mt="0">
          {formatMediumDate(parseISO(cisaKnownExploited.added_date.slice(0, -5)))}
        </P>
      </Box>
      <Box flexBasis="33%">
        <P textStyle="bodyMain" color="colorTextDeemphasis" mt="0" mb="spaceGapMedium">
                Required Remediation Date
        </P>
        <P textStyle="bodyMain" color="colorTextMain" mt="0">
          {formatMediumDate(parseISO(cisaKnownExploited.due_date.slice(0, -5)))}
        </P>
      </Box>
    </Box>
  )
}

export const MitigationRow = ({ mitigation, disclosureDate, daysToPatch }) => {
  return (
    <Box display='flex' flexWrap="wrap">
      { mitigation
        ? <Box flexBasis="33%">
          <P textStyle="bodyMain" color="colorTextDeemphasis" mt="0" mb="spaceGapMedium">
                Mitigation
          </P>
          <P textStyle="bodyMain" color="colorTextMain" mt="0">
            <MitigationWithFreemium mitigation={mitigation} />
          </P>
        </Box>
        : null
      }
      { disclosureDate
        ? <Box flexBasis="33%">
          <P textStyle="bodyMain" color="colorTextDeemphasis" mt="0" mb="spaceGapMedium">
                Date of Disclosure
          </P>
          <P textStyle="bodyMain" color="colorTextMain" mt="0">
            <DisclosureDateWithFreemium disclosureDate={formatMediumDate(parseISO(disclosureDate.slice(0, -5)))} />
          </P>
        </Box>
        : null
      }
      {
        daysToPatch !== null && String(daysToPatch)
          ? <Box flexBasis="33%">
            <P textStyle="bodyMain" color="colorTextDeemphasis" mt="0" mb="spaceGapMedium">
            Days to Patch
              <Tooltip
                ml="spaceGapSmall"
                color="white"
                portal
                size="large"
                textStyle="bodyMain"
                tooltip='Days to Patch is the number of days between the date of disclosure and the first vendor fix.'
              >
                <Icon name="info" verticalAlign="top" />
              </Tooltip>
            </P>
            <P textStyle="bodyMain" color="colorTextMain" mt="0">
              <DaysToPatchWithFreemium daysToPatch={daysToPatch} />
            </P>
          </Box>
          : null
      }
    </Box>
  )
}

export const ExploitationRow = ({ exploitConsequence, exploitVectors }) => {
  return (
    <Box display='flex' flexWrap="wrap">
      { exploitConsequence
        ? <Box flexBasis="33%">
          <P textStyle="bodyMain" color="colorTextDeemphasis" mt="0" mb="spaceGapMedium">
                Exploitation Consequence
          </P>
          <P textStyle="bodyMain" color="colorTextMain" mt="0">
            <ExploitConsequenceWithFreemium exploitConsequence={exploitConsequence} />
          </P>
        </Box>
        : null
      }
      { exploitVectors
        ? <Box flexBasis="33%">
          <P textStyle="bodyMain" color="colorTextDeemphasis" mt="0" mb="spaceGapMedium">
          Exploitation Vectors
          </P>
          <ListGrid list={exploitVectors} cellWidth={['100%']} mt="0">
            {item => <ListGridItem>{item}</ListGridItem>}
          </ListGrid>
        </Box>
        : null
      }
    </Box>
  )
}

export const WorkArounds = ({ workarounds }) => {
  return workarounds.map((workaround, index) => (
      <WorkaroundsWithFreemium key={index} workarounds={workaround} />
  ))
}
